import { Injectable } from '@angular/core';
import { BaseService } from 'src/app/base/base.service';
import { PlayService } from '../play/play.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, map, BehaviorSubject, Subscription, tap } from 'rxjs';
import { AppSettings } from 'src/app/app-settings';
import { Howl, Howler } from 'howler';
import { QuestionsService } from '../play/questions/questions.service';
import { RhythmCategory, Rhythm, RhythmDetails, RhythmDescriptor } from './models/rhythm';
import { RhythmView } from './models/rhythm-view'

interface RhythmDetailsDataModel {
  "fields": RhythmDetailsFieldsDataModel[];
  "media": RhythmDetailsMediaDataModel[];
  "additions": any;
}

interface RhythmDetailsFieldsDataModel {
  "id": number;
  "id_field": number;
  "name": string;
  "value": string;
}

interface RhythmDetailsMediaDataModel {
  "id": number;
  "step": number;
  "url": string;
}

interface RhythmCategoriesDataModel {
  "name": string;
  "fullName": string;
  "short_description": string;
  "long_description": string;
}

@Injectable()
export class RhythmService extends BaseService {

    public httpHeaders: HttpHeaders;
    public httpOptions: { headers: HttpHeaders };

    public IMPORT_ENDPOINT: any = AppSettings.IMPORT_ENDPOINT;
    
    public questionMediaAnimation: boolean = false;

    public pausedGame: boolean = false;
    public showMenu: boolean = false;
    public gameSounds: Array<any> = [];
    public gameSoundMute: boolean = false;

    public rhythmsSubject: Subject<Array<Rhythm>> = new Subject<Array<Rhythm>>();
    public rhythmCategoriesSubject: BehaviorSubject<Array<RhythmCategory>> = new BehaviorSubject<Array<RhythmCategory>>([]);
    public rhythmDescriptorsSubject: BehaviorSubject<Array<RhythmDescriptor>> = new BehaviorSubject<Array<RhythmDescriptor>>([]);
    public selectedRhythmCategorySubject: BehaviorSubject<RhythmCategory> = new BehaviorSubject<RhythmCategory>(null);
    public selectedRhythmSubject: BehaviorSubject<Rhythm> = new BehaviorSubject<Rhythm>(null);
    public selectedViewSubject: BehaviorSubject<RhythmView> = new BehaviorSubject<RhythmView>(RhythmView.RHYTHMS);

    public hideDeepDiveSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public onWindowWidthChange: BehaviorSubject<number> = new BehaviorSubject<number>(window.innerWidth);
    
    constructor(
      public http: HttpClient,
      public questionsService: QuestionsService
    ) {
        super(http);
        this.ROOT_ENDPOINT += 'rhythm/';
        this.httpHeaders = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': 'Bearer keyTXuKSfFp0fj3dC'});
        this.httpOptions = { headers: this.httpHeaders };
        this.loadGameSounds();

        this.selectedRhythmCategorySubject.subscribe((rhythmCategory: RhythmCategory) => {
          if(rhythmCategory && rhythmCategory.rhythms.length > 0){
            if(this.selectedRhythmSubject.value == null){
              this.selectedRhythmSubject.next(rhythmCategory.rhythms[0]);
            }else{
              let selectedRhythm = rhythmCategory.rhythms.find((rhythm: Rhythm) => {
                if(rhythm.id == this.selectedRhythmSubject.value.id){
                  return true;
                }else{
                  return false;
                }
              })

              if(selectedRhythm){
                this.selectedRhythmSubject.next(selectedRhythm);
              }else{
                this.selectedRhythmSubject.next(rhythmCategory.rhythms[0]);
              }
            }
          }
        })
    }

    public get(id: number, extraId: string = ''): Observable<Array<any>> {
        return this.http
            .get(this.ROOT_ENDPOINT + 'search/'+ id + '/' + extraId, this.options)
            .pipe(
              map((res: any) => res),
            )
    }

    public getRhythms(): Observable<Array<Rhythm>> {
      return this.http.get(this.ROOT_ENDPOINT, this.options).pipe(
        map((res: Rhythm[]) => {
          return res;
        }),
        tap((rhythms: Array<Rhythm>) => {
          console.log(rhythms)
          let rhythmCategories = this.rhythmCategoriesSubject.value
          rhythmCategories.forEach((category: RhythmCategory, index:number) => {
            rhythmCategories[index].rhythms = rhythms.filter((rhythm: Rhythm) => rhythm.categories.includes(category.full_name));
          })
          console.log(rhythmCategories)
          this.rhythmCategoriesSubject.next(rhythmCategories);
        })
      )
    }

    public getRhythmCategories(): Observable<Array<RhythmCategory>> {
      return this.http.get(this.ROOT_ENDPOINT + 'category', this.options).pipe(
        map((res: RhythmCategoriesDataModel[]) => {
          let rhythmCategories: Array<RhythmCategory> = [];
          res.forEach((category: RhythmCategoriesDataModel) => {
            let type:string;

            if(category.fullName == 'sinus rhythm' || category.fullName == 'sinus bradycardia' || category.fullName == 'sinus tachycardia' || category.fullName == 'sinus arrhythmia' || category.fullName == 'wandering pacemaker'){
              type = 'SINUS'
            }else if (category.fullName == 'atrial fibrillation' || category.fullName == 'atrial flutter' || category.fullName == 'supraventricular tachycardia' || category.fullName == 'junct brady | junct rhythm' || category.fullName == 'junct tachy | accel junct'){
              type = 'ATR/JUNCT'
            }else if (category.fullName == 'second degree AV block Type I' || category.fullName == 'second degree AV block Type II' || category.fullName == 'second degree AV block 2:1' || category.fullName == 'third degree AV block' || category.fullName == 'ventricular standstill'){
              type = 'AVB'
            }else if (category.fullName == 'paced atrial rhythm' || category.fullName == 'paced ventricular rhythm' || category.fullName == 'dual chamber paced rhythm' || category.fullName == 'atrial sensed vent paced' || category.fullName == 'failure to capture'){
              type = 'PACED'
            }else if (category.fullName == 'vent rhythm | accel vent' || category.fullName == 'ventricular tachycardia' || category.fullName == 'ventricular fibrillation' || category.fullName == 'torsades de pointes' || category.fullName == 'agonal rhythm | asystole'){
              type = 'VENT'
            }else if(['abnormal P wave', 'prolonged PR', 'short PR interval', 'IVCD','long QT interval','ST elevation', 'ST depression', 'T wave inversion', 'abnormal T wave', 'pathological Q Wave'].includes(category.fullName)){
              type = 'PQRST EXTRAS'
            }else if(['premature atrial complex', 'nonconducted PAC', 'ectopic atrial', 'premature junctional', 'junctional escape', 'premature ventricular', 'multifocal PVC', 'ventricular escape', 'ventricular couplet', 'non-sustained VT', 'vent bigeminy | trigeminy', 'fusion complex', 'sinus pause | arrest', 'sinus exit block', 'artifact'].includes(category.fullName)){
              type = 'ECTOPIC EXTRAS'
            }else if(['normal', 'implanted pacemaker', 'lateral STEMI', 'right ventricular STEMI', 'inferior STEMI', 'anterior STEMI', 'STEMI equivalents', 'ACS imposters', 'beyond ACS', 'bundle branch block', 'posterior STEMI', 'NSTE-ACS'].includes(category.fullName)){
              type = 'Acute Coronary Syndromes'
            }

            rhythmCategories.push({
              name: category.name,
              full_name: category.fullName,
              short_description: category.short_description,
              long_description: category.long_description,
              rhythms: [],
              type: type
            });
          });
          return rhythmCategories;
        }),
        map((rhythmCategories: Array<RhythmCategory>) => {
          let lead12Ordering: string[] = ['normal', 'anterior STEMI', 'lateral STEMI', 'inferior STEMI', 'posterior STEMI', 'right ventricular STEMI', 'STEMI equivalents', 'NSTE-ACS', 'ACS imposters', 'bundle branch block', 'implanted pacemaker', 'beyond ACS'];
          // Sort rhythmCategories so that if element is in lead12Ordering, it will be ordered by lead12Ordering
          rhythmCategories.sort((a: RhythmCategory, b: RhythmCategory) => {
            let aIndex = lead12Ordering.indexOf(a.full_name);
            let bIndex = lead12Ordering.indexOf(b.full_name);
            if(aIndex == -1 && bIndex == -1){
              return 0;
            }else if(aIndex == -1){
              return 1;
            }else if(bIndex == -1){
              return -1;
            }else{
              return aIndex - bIndex;
            }
          });
          return rhythmCategories;
        }),
        map((rhythmCategories: Array<RhythmCategory>) => {
          let extrasOrdering: string[] = ['abnormal P wave', 'prolonged PR', 'short PR interval', 'IVCD','long QT interval','ST elevation', 'ST depression', 'T wave inversion', 'abnormal T wave', 'pathological Q Wave',
          'premature atrial complex', 'nonconducted PAC', 'ectopic atrial', 'premature junctional', 'junctional escape', 'premature ventricular', 'multifocal PVC', 'ventricular escape', 'ventricular couplet', 'non-sustained VT', 'vent bigeminy | trigeminy', 'fusion complex', 'sinus pause | arrest', 'sinus exit block', 'artifact'];
          // Sort rhythmCategories so that if element is in extrasOrdering, it will be ordered by extrasOrdering
          rhythmCategories.sort((a: RhythmCategory, b: RhythmCategory) => {
            let aIndex = extrasOrdering.indexOf(a.full_name);
            let bIndex = extrasOrdering.indexOf(b.full_name);
            if(aIndex == -1 && bIndex == -1){
              return 0;
            }else if(aIndex == -1){
              return 1;
            }else if(bIndex == -1){
              return -1;
            }else{
              return aIndex - bIndex;
            }
          });
          return rhythmCategories;
        }),
        tap((rhythmCategories: Array<RhythmCategory>) => {
          this.rhythmCategoriesSubject.next(rhythmCategories);
          console.log(this.rhythmCategoriesSubject.value)
        })
      )
    }

    public getRhythmDescriptors(): Observable<Array<RhythmDescriptor>> {
      return this.http.get(this.ROOT_ENDPOINT + 'descriptors', this.options).pipe(
        map((res: Array<RhythmDescriptor>) => {
          return res;
        })
      )
    }

    public getRhythmDetails(rhythmId: string): Observable<RhythmDetails> {
      return this.http.get(this.ROOT_ENDPOINT+'details/'+rhythmId , this.options).pipe(
        map((res: RhythmDetailsDataModel) => {
          
          let title: string;
          let description: string;
          let pdf_name: string;
          let last_updated: string;
          let r_hr_full: string;
          let r_qrs_full: string;
          let r_pwv_full: string;
          let step0_media: string;
          let step1_media: string;
          let step2_media: string;
          let step3_media: string;
          let f_suggested_actions: string;
          let f_credits: string;
          let long_description: string;

          let prior_12_lead_ecg_image: string;

          let full_rhythm_name: string;
          let hertz: number;
          let pr_interval: number;
          let qrs_interval: number;
          let qtc: number;
          let print_speed: number;
          let heart_rate: number;

          try{
            title = res.fields.find(field => field.id_field === 2).value;
          }catch(e){
            title = '';
          }

          try{
            description = res.fields.find(field => field.id_field === 30).value;
          }catch(e){
            description = '';
          }

          try{
            pdf_name = res.media.find(media => media.step === 0).url;
          }catch(e){
            pdf_name = '';
          }

          try{
            last_updated = res.fields.find(field => field.id_field === 6).value;
          }catch(e){
            last_updated = '';
          }

          try{
            r_hr_full = res.fields.find(field => field.id_field === 19).value;
          }catch(e){
            r_hr_full = '';
          }

          try{
            r_qrs_full = res.fields.find(field => field.id_field === 20).value;
          }catch(e){
            r_qrs_full = '';
          }

          try{
            r_pwv_full = res.fields.find(field => field.id_field === 21).value;
          }catch(e){
            r_pwv_full = '';
          }

          try{
            step0_media = res.media.find(media => media.step === 4).url;
          }catch(e){
            step0_media = '';
          }

          try{
            step1_media = res.media.find(media => media.step === 1).url;
          }catch(e){
            step1_media = '';
          }

          try{
            step2_media = res.media.find(media => media.step === 2).url;
          }catch(e){
            step2_media = '';
          }

          try{
            step3_media = res.media.find(media => media.step === 3).url;
          }catch(e){
            step3_media = '';
          }

          try{
            f_suggested_actions = res.fields.find(field => field.id_field === 24).value;
          }catch(e){
            f_suggested_actions = '';
          }

          try{
            f_credits = res.fields.find(field => field.id_field === 25).value;
          }catch(e){
            f_credits = '';
          }

          try{
            long_description = res.fields.find(field => field.id_field === 22).value;
          }catch(e){
            long_description = '';
          }

          try{
            prior_12_lead_ecg_image = res.additions.prior_12_lead_ecg_image;
          }catch(e){
            prior_12_lead_ecg_image = '';
          }

          try{
            full_rhythm_name = res.additions.twelve_lead_details.full_rhythm_name;
          }catch(e){
            full_rhythm_name = '';
          }

          try{
            hertz = res.additions.twelve_lead_details.hertz;
          }catch(e){
            hertz = 0;
          }

          try{
            pr_interval = res.additions.twelve_lead_details.pr_interval;
          }catch(e){
            pr_interval = 0;
          }

          try{
            qrs_interval = res.additions.twelve_lead_details.qrs_interval;
          }catch(e){
            qrs_interval = 0;
          }

          try{
            qtc = res.additions.twelve_lead_details.qtc;
          }catch(e){
            qtc = 0;
          }

          try{
            print_speed = res.additions.twelve_lead_details.print_speed;
          }catch(e){
            print_speed = 0;
          }

          try{
            heart_rate = res.additions.twelve_lead_details.heart_rate;
          }catch(e){
            heart_rate = 0;
          }

          let rhythmDetails: RhythmDetails = {
            cid: '',
            title: title,
            otherRhythms: [],
            extras: [],
            description: description,
            pdf_name: pdf_name,
            last_updated: last_updated,
            r_hr: 0,
            r_hr_full: r_hr_full,
            r_qrs_full: r_qrs_full,
            r_pwv_full: r_pwv_full,
            step0_media: step0_media,
            step1_media: step1_media,
            step2_media: step2_media,
            step3_media: step3_media,
            f_suggested_actions: f_suggested_actions,
            f_credits: f_credits,
            long_description: long_description,
            extras_name: '',
            extras_description: '',
            prior_12_lead_ecg_image: prior_12_lead_ecg_image,
            full_rhythm_name: full_rhythm_name,
            hertz: hertz,
            pr_interval: pr_interval,
            qrs_interval: qrs_interval,
            qtc: qtc,
            print_speed: print_speed,
            heart_rate: heart_rate
          }

          console.log(rhythmDetails)

          return rhythmDetails;
        })
      )
    }

    public nextRhythm(): void {
      let rhythmCategory: RhythmCategory = this.selectedRhythmCategorySubject.value;
      let rhythm: Rhythm = this.selectedRhythmSubject.value;
      let rhythmIndex: number = rhythmCategory.rhythms.indexOf(rhythm);
      let nextRhythmIndex: number = rhythmIndex + 1;
      if(nextRhythmIndex >= rhythmCategory.rhythms.length){
        nextRhythmIndex = 0;
      }
      this.selectedRhythmSubject.next(rhythmCategory.rhythms[nextRhythmIndex]);
    }

    public hideDeepDive(hide:boolean): void {
      this.hideDeepDiveSubject.next(hide);
    }

    public getExtrasFromRhythm(id: string): Observable<Array<any>> {
      return this.http.get(this.ROOT_ENDPOINT + 'extrasFromRhythm/'+id, this.options).pipe(
        map((res: any) => res)
      )
    }

    public toggleMenu(pauseToggle = false): void {
      if(pauseToggle) this.pausedGame = pauseToggle;

      this.questionMediaAnimation = !this.questionMediaAnimation;
      this.questionsService.showMenu = !this.questionsService.showMenu;
    }
    
    public triggerSound(fileName): void{
      if(this.gameSounds[fileName] && !this.gameSoundMute){
        this.gameSounds[fileName].play();
        console.log('Playing sound: ' + fileName);
      }
    }

    public loadGameSounds(): void {
      this.gameSounds['button-click'] = new Howl({
        src: [AppSettings.IMPORT_ENDPOINT + '/airtable-import/sounds/button-click-sound.wav']
      });
      this.gameSounds['last-question'] = new Howl({
        src: [AppSettings.IMPORT_ENDPOINT + '/airtable-import/sounds/last-question-sound.mp3']
      });
      this.gameSounds['correct-answer'] = new Howl({
        src: [AppSettings.IMPORT_ENDPOINT + '/airtable-import/sounds/correct-answer-sound.mp3']
      });
      this.gameSounds['incorrect-answer'] = new Howl({
        src: [AppSettings.IMPORT_ENDPOINT + '/airtable-import/sounds/incorrect-answer-sound.mp3']
      });
      this.gameSounds['feedback'] = new Howl({
        src: [AppSettings.IMPORT_ENDPOINT + '/airtable-import/sounds/feedback-sound.mp3']
      });
      this.gameSounds['fireworks-small'] = new Howl({
        src: [AppSettings.IMPORT_ENDPOINT + '/airtable-import/sounds/fireworks---small-sound.wav']
      });
      this.gameSounds['hr-beep'] = new Howl({
        src: ['assets/sounds/heartRatebeep.mp3']
      });
    }
}